import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus, Trash2, Mail, User, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '../context/AuthContext';
import BrandingFooter from '@/components/BrandingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TeamManagementPage() {
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'user' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchTeamMembers();
  }, [user]);

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeaders()
      });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/auth/invite`, {
        email: formData.email,
        name: formData.name,
        role: formData.role
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success(`Team member invited! Temporary password: ${response.data.temporary_password}`);
      setFormData({ name: '', email: '', password: '', role: 'user' });
      setShowAddForm(false);
      fetchTeamMembers();
    } catch (error) {
      console.error('Error adding member:', error);
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm('Are you sure? This will delete all their transcriptions.')) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${memberId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Team member deleted');
      fetchTeamMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Failed to delete team member');
    }
  };

  const handleResetPassword = async (memberId, memberName) => {
    if (!window.confirm(`Reset password for ${memberName}?`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/users/${memberId}/reset-password`, {}, {
        headers: getAuthHeaders()
      });
      
      const tempPassword = response.data.temporary_password;
      
      // Copy to clipboard
      navigator.clipboard.writeText(tempPassword);
      
      toast.success(
        <div>
          <p className="font-bold">Password reset!</p>
          <p className="text-sm">New password: {tempPassword}</p>
          <p className="text-xs mt-1">Copied to clipboard!</p>
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#007AFF] mx-auto animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative">
      <div className="hero-glow absolute top-0 left-0 right-0 h-[400px]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Button onClick={() => navigate('/')} className="btn-secondary mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-font text-4xl font-bold uppercase text-white mb-2">Team Management</h1>
              <p className="text-[#A1A1AA]">Manage your team members</p>
            </div>
            <Button onClick={() => setShowAddForm(!showAddForm)} className="btn-primary">
              <UserPlus className="w-5 h-5 mr-2" />
              Add Member
            </Button>
          </div>
        </div>

        {showAddForm && (
          <div className="glass-card rounded-xl p-6 mb-8">
            <h3 className="heading-font text-xl font-bold text-white uppercase mb-4">Add New Team Member</h3>
            <form onSubmit={handleAddMember} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#27272A] text-white"
                  required
                />
                <Input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-[#1A1A1A] border-[#27272A] text-white"
                  required
                />
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="px-3 py-2 bg-[#1A1A1A] border border-[#27272A] text-white rounded-md"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <p className="text-[#52525B] text-sm">A temporary password will be generated and displayed</p>
              <div className="flex gap-3">
                <Button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Inviting...' : 'Invite Member'}
                </Button>
                <Button type="button" onClick={() => setShowAddForm(false)} className="btn-secondary">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="space-y-3">
          {teamMembers.map((member) => (
            <div key={member.id} className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <User className="w-5 h-5 text-[#007AFF]" />
                    <h3 className="text-white font-medium text-lg">{member.name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      member.role === 'admin' 
                        ? 'bg-[#007AFF]/20 text-[#007AFF]'
                        : 'bg-[#3F3F46]/20 text-[#A1A1AA]'
                    }`}>
                      {member.role.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                    <div className="flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {member.email}
                    </div>
                  </div>
                </div>

                {member.id !== user?.id && (
                  <div className="flex gap-2 ml-4">
                    <Button
                      onClick={() => handleResetPassword(member.id, member.name)}
                      className="btn-secondary"
                      size="sm"
                    >
                      Reset Password
                    </Button>
                    <Button
                      onClick={() => handleDeleteMember(member.id)}
                      className="btn-secondary"
                      size="sm"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <BrandingFooter />
    </div>
  );
}
